export interface ApiErrorResponse {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Status used for failures that never reached the server. */
export const NETWORK_ERROR_STATUS = 0;

/** Paths served directly by the API rather than under the `/api` prefix. */
const UNPREFIXED_PREFIXES = ["/media"];

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

class ApiClient {
  constructor(private readonly base = "") {}

  private url(path: string): string {
    return UNPREFIXED_PREFIXES.some((prefix) => path.startsWith(prefix))
      ? path
      : `${this.base}${path}`;
  }

  private async parseError(res: Response): Promise<ApiErrorResponse> {
    try {
      return (await res.json()) as ApiErrorResponse;
    } catch {
      return {
        code: "HTTP_ERROR",
        message: res.statusText || `Request failed with status ${res.status}`,
      };
    }
  }

  private async parseSuccess<T>(res: Response): Promise<T> {
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    if (!text) return undefined as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      throw new ApiError("PARSE_ERROR", "Invalid response from server", res.status);
    }
  }

  private async request<T>(method: Method, path: string, init: RequestInit = {}): Promise<T> {
    let res: Response;
    try {
      res = await fetch(this.url(path), { method, credentials: "include", ...init });
    } catch {
      // fetch only rejects when the request never completed. Reporting that as
      // an ApiError means every caller's existing error branch shows something
      // truer than "please try again".
      throw new ApiError(
        "NETWORK_ERROR",
        "Could not reach the server. Check your connection and try again.",
        NETWORK_ERROR_STATUS,
      );
    }
    if (!res.ok) {
      const err = await this.parseError(res);
      throw new ApiError(err.code, err.message, res.status, err.details);
    }
    return this.parseSuccess<T>(res);
  }

  private jsonInit(body: unknown): RequestInit {
    if (body === undefined) return {};
    return {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    };
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>("GET", path);
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("POST", path, this.jsonInit(body));
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("PATCH", path, this.jsonInit(body));
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("PUT", path, this.jsonInit(body));
  }

  delete<T = void>(path: string): Promise<T> {
    return this.request<T>("DELETE", path);
  }

  upload<T>(path: string, formData: FormData): Promise<T> {
    return this.request<T>("POST", path, { body: formData });
  }
}

export const api = new ApiClient("/api");
